import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'auth/AuthProvider';
import { useI18n } from 'core/i18n/I18nProvider';
import { ApiError } from 'core/errors/ApiError';
import { firstAccessiblePath, safeReturnTo } from 'core/routing/paths';
import { Button } from 'shared/components/Button';
import { TextField } from 'shared/forms/Fields';

export function LoginPage() {
  const { t, locale, setLocale } = useI18n();
  const { login, sessionMessage } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(sessionMessage ? t(sessionMessage) : '');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setFormError('');
    setSubmitting(true);
    try {
      const data = await login({ email, password });
      const fallback = firstAccessiblePath(data?.admin || []);
      navigate(safeReturnTo(params.get('returnTo'), fallback), { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.isValidation()) {
        setErrors(error.errors || {});
        setFormError(error.message);
      } else if (error instanceof ApiError && error.status === 401) {
        setFormError(t('auth.invalid'));
      } else if (error instanceof ApiError && error.isThrottled()) {
        setFormError(t('errors.throttled'));
      } else {
        setFormError(error.message || t('errors.unexpected'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-shell">
      <form className="login-card" onSubmit={onSubmit} noValidate aria-label={t('auth.title')}>
        <div className="login-lang">
          <button type="button" className={locale === 'ar' ? 'active' : ''} onClick={() => setLocale('ar')}>
            {t('language.ar')}
          </button>
          <button type="button" className={locale === 'en' ? 'active' : ''} onClick={() => setLocale('en')}>
            {t('language.en')}
          </button>
        </div>
        <p className="brand-mark">{t('appName')}</p>
        <h1>{t('auth.title')}</h1>
        <p className="muted">{t('auth.subtitle')}</p>
        {formError ? <p className="form-banner" role="alert">{formError}</p> : null}
        <TextField
          id="email"
          label={t('auth.email')}
          type="email"
          value={email}
          onChange={setEmail}
          error={errors.email?.[0]}
          required
          autoComplete="username"
        />
        <TextField
          id="password"
          label={t('auth.password')}
          type="password"
          value={password}
          onChange={setPassword}
          error={errors.password?.[0]}
          required
          autoComplete="current-password"
        />
        <Button type="submit" loading={submitting} disabled={submitting}>
          {t('actions.login')}
        </Button>
      </form>
    </main>
  );
}
